import { Extension, Mark, mergeAttributes } from "@tiptap/core";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { Placeholder } from "@tiptap/extensions";
import {
  CLASSES_DE_ALINHAMENTO,
  CLASSES_DE_COR,
  CLASSES_DE_FONTE,
  CLASSES_DE_REALCE,
  CLASSES_DE_RECUO,
  CLASSES_DE_TAMANHO,
  TAMANHOS_DO_TEXTO,
} from "@noesis/shared";

/**
 * As marcas do editor do caderno — **classe, nunca atributo `style`**.
 *
 * Este arquivo é a razão de o Tiptap ter passado no portão de CSP. As extensões
 * de cor, realce e alinhamento que vêm prontas na família ProseMirror
 * serializam o valor como estilo inline (`style="color: …"`,
 * `style="text-align: …"`), e a CSP do Noesis é `style-src 'self'` **sem**
 * `style-src-attr`: o navegador bloquearia o estilo e a falha apareceria no
 * conteúdo do usuário — um parágrafo sem cor, sem erro nenhum no console.
 *
 * Aqui cada marca devolve uma **classe do vocabulário fechado** da RN-40, que
 * mora em `packages/shared/src/conteudo.ts` junto do saneador que a valida na
 * gravação e na leitura. Vocabulário, saneador e editor citam a mesma lista: é
 * isso que impede o editor de produzir uma classe que o saneador vai jogar fora
 * na hora de gravar.
 *
 * O atributo é a **posição na paleta**, não o valor. `nivel: 2` continua
 * significando a mesma coisa quando o tema troca de tinta; um `#c33` guardado
 * dentro da nota viraria uma cor estranha no tema claro e um valor morto no dia
 * em que a paleta mudar.
 *
 * `apps/web/src/caderno/portao-csp.portao.test.ts` é o portão automatizado:
 * ele monta o editor de verdade, aplica todo comando da barra de ferramentas e
 * falha se sair um `style=` na serialização. Portão que roda uma vez, na mão,
 * não protege a segunda vez que alguém acrescentar uma extensão.
 */

/** `rico-cor-1` … `rico-cor-4`. O índice do array é `nivel - 1`. */
export type NivelDePaleta = 1 | 2 | 3 | 4;
export const NIVEIS: readonly NivelDePaleta[] = [1, 2, 3, 4];

/**
 * Um degrau de tamanho. Os dois antigos (`p`, `g`) continuam porque notas
 * gravadas antes desta rodada os carregam; os seis numerados são o que o menu
 * de tamanho oferece agora.
 */
export type EscalaDeTexto = "p" | "g" | (typeof TAMANHOS_DO_TEXTO)[number];

/** Família de trecho. `null` é a fonte padrão da interface. */
export type FamiliaDoTexto = "serifa" | "mono";

function classeDeTamanho(escala: unknown): string | null {
  if (escala === "p" || escala === "g") return `rico-${escala}`;
  const n = Number(escala);
  return (TAMANHOS_DO_TEXTO as readonly number[]).includes(n) ? `rico-tam-${n}` : null;
}

function tamanhoPorClasse(el: HTMLElement): EscalaDeTexto | false {
  if (el.classList.contains("rico-p")) return "p";
  if (el.classList.contains("rico-g")) return "g";
  for (const n of TAMANHOS_DO_TEXTO) if (el.classList.contains(`rico-tam-${n}`)) return n;
  return false;
}

function classePorNivel(lista: readonly string[], nivel: unknown): string | null {
  const n = Number(nivel);
  return Number.isInteger(n) && n >= 1 && n <= lista.length ? lista[n - 1]! : null;
}

function nivelPorClasse(lista: readonly string[], el: HTMLElement): NivelDePaleta | false {
  const i = lista.findIndex((c) => el.classList.contains(c));
  return i === -1 ? false : ((i + 1) as NivelDePaleta);
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    ricoCor: {
      definirCor: (nivel: NivelDePaleta) => ReturnType;
      limparCor: () => ReturnType;
    };
    ricoRealce: {
      definirRealce: (nivel: NivelDePaleta) => ReturnType;
      limparRealce: () => ReturnType;
    };
    ricoTamanho: {
      definirTamanho: (escala: EscalaDeTexto) => ReturnType;
      limparTamanho: () => ReturnType;
    };
    ricoFonte: {
      definirFonte: (familia: FamiliaDoTexto | null) => ReturnType;
    };
    ricoAlinhamento: {
      definirAlinhamento: (classe: string | null) => ReturnType;
    };
    ricoRecuo: {
      aumentarRecuo: () => ReturnType;
      diminuirRecuo: () => ReturnType;
    };
  }
}

/** Cor de texto. `<span class="rico-cor-N">`, nunca `style="color: …"`. */
export const CorDoTexto = Mark.create({
  name: "ricoCor",

  addAttributes() {
    return {
      nivel: {
        default: 1,
        parseHTML: (el) => nivelPorClasse(CLASSES_DE_COR, el),
        // `false` tira o atributo da serialização automática: quem escreve o
        // HTML é o `renderHTML` abaixo, e um `nivel="2"` solto no documento
        // seria atributo fora da lista de permissão do saneador.
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return CLASSES_DE_COR.map((c) => ({ tag: `span.${c}` }));
  },

  renderHTML({ HTMLAttributes, mark }) {
    const classe = classePorNivel(CLASSES_DE_COR, mark.attrs.nivel);
    return ["span", mergeAttributes(HTMLAttributes, { class: classe ?? CLASSES_DE_COR[0] }), 0];
  },

  addCommands() {
    return {
      definirCor:
        (nivel) =>
        ({ commands }) =>
          commands.setMark(this.name, { nivel }),
      limparCor:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

/**
 * Realce. Sai como `<mark>`, e não `<span>`, de propósito: `mark` é o elemento
 * que o HTML tem para "trecho destacado", então o leitor de tela e o Markdown
 * de exportação lidam com ele sem precisar da classe.
 */
export const Realce = Mark.create({
  name: "ricoRealce",

  addAttributes() {
    return {
      nivel: {
        default: 1,
        parseHTML: (el) => nivelPorClasse(CLASSES_DE_REALCE, el),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return CLASSES_DE_REALCE.map((c) => ({ tag: `mark.${c}` }));
  },

  renderHTML({ HTMLAttributes, mark }) {
    const classe = classePorNivel(CLASSES_DE_REALCE, mark.attrs.nivel);
    return ["mark", mergeAttributes(HTMLAttributes, { class: classe ?? CLASSES_DE_REALCE[0] }), 0];
  },

  addCommands() {
    return {
      definirRealce:
        (nivel) =>
        ({ commands }) =>
          commands.setMark(this.name, { nivel }),
      limparRealce:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

/**
 * Tamanho — **seis pontos fixos, não uma escala livre**.
 *
 * A Fase 2 pede o menu de 12 a 24, e ele cabe: a escala continua fechada, e o
 * que ela oferece é um conjunto que o vocabulário e a folha conhecem por nome.
 * Um campo de pixel livre seria outra coisa — o usuário reescrevendo a
 * tipografia do app um parágrafo por vez, sem nada garantindo contraste nem
 * medida. Os dois degraus antigos (`rico-p`, `rico-g`) continuam sendo lidos,
 * porque notas gravadas antes desta rodada os carregam.
 */
export const TamanhoDoTexto = Mark.create({
  name: "ricoTamanho",

  addAttributes() {
    return {
      escala: {
        default: 16 as EscalaDeTexto,
        parseHTML: (el) => tamanhoPorClasse(el),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return CLASSES_DE_TAMANHO.map((c) => ({ tag: `span.${c}` }));
  },

  renderHTML({ HTMLAttributes, mark }) {
    const classe = classeDeTamanho(mark.attrs.escala);
    return ["span", mergeAttributes(HTMLAttributes, { class: classe ?? "rico-tam-16" }), 0];
  },

  addCommands() {
    return {
      definirTamanho:
        (escala) =>
        ({ commands }) =>
          commands.setMark(this.name, { escala }),
      limparTamanho:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

/**
 * Família do trecho: padrão, serifada ou mono.
 *
 * Marca e não atributo de bloco, ao contrário do alinhamento: trocar a fonte de
 * meia frase existe — é o que se faz para citar um identificador no meio de um
 * parágrafo. `null` remove a marca e devolve o trecho à fonte da interface.
 */
export const FonteDoTexto = Mark.create({
  name: "ricoFonte",

  addAttributes() {
    return {
      familia: {
        default: "serifa" as FamiliaDoTexto,
        parseHTML: (el) => (el.classList.contains("rico-fonte-mono") ? "mono" : el.classList.contains("rico-fonte-serifa") ? "serifa" : false),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return CLASSES_DE_FONTE.map((c) => ({ tag: `span.${c}` }));
  },

  renderHTML({ HTMLAttributes, mark }) {
    const familia = mark.attrs.familia === "mono" ? "mono" : "serifa";
    return ["span", mergeAttributes(HTMLAttributes, { class: `rico-fonte-${familia}` }), 0];
  },

  addCommands() {
    return {
      definirFonte:
        (familia) =>
        ({ commands }) =>
          familia === null ? commands.unsetMark(this.name) : commands.setMark(this.name, { familia }),
    };
  },
});

/**
 * Alinhamento. É atributo de **bloco**, não marca: alinhar meia frase não
 * existe. Ele entra como atributo global do parágrafo e do título, e sai como
 * classe na tag do próprio bloco.
 *
 * A ausência de classe é o alinhamento padrão, herdado da folha. Não existe
 * `rico-esquerda`: um vocabulário que nomeia o padrão obriga todo bloco a
 * carregar uma classe para dizer "nada de especial".
 */
export const Alinhamento = Extension.create({
  name: "ricoAlinhamento",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading"],
        attributes: {
          alinhamento: {
            default: null,
            parseHTML: (el) => CLASSES_DE_ALINHAMENTO.find((c) => el.classList.contains(c)) ?? null,
            renderHTML: (attrs) =>
              typeof attrs.alinhamento === "string" && (CLASSES_DE_ALINHAMENTO as readonly string[]).includes(attrs.alinhamento)
                ? { class: attrs.alinhamento }
                : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      definirAlinhamento:
        (classe) =>
        ({ commands }) =>
          commands.updateAttributes("paragraph", { alinhamento: classe }) ||
          commands.updateAttributes("heading", { alinhamento: classe }),
    };
  },
});

/**
 * Recuo de parágrafo **fora de lista**, em três degraus.
 *
 * Dentro de lista quem recua é `sinkListItem`/`liftListItem`, que muda a
 * estrutura do documento — é aninhamento de verdade, e é o que o Tab faz lá.
 * Fora dela não existe estrutura para aninhar: um parágrafo recuado é um
 * parágrafo com uma margem, e a margem vira classe, como toda formatação aqui.
 */
export const Recuo = Extension.create({
  name: "ricoRecuo",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading"],
        attributes: {
          recuo: {
            default: 0,
            parseHTML: (el) => {
              const i = CLASSES_DE_RECUO.findIndex((c) => el.classList.contains(c));
              return i === -1 ? 0 : i + 1;
            },
            renderHTML: (attrs) => {
              const n = Number(attrs.recuo);
              const classe = Number.isInteger(n) && n >= 1 && n <= CLASSES_DE_RECUO.length ? CLASSES_DE_RECUO[n - 1] : null;
              return classe ? { class: classe } : {};
            },
          },
        },
      },
    ];
  },

  addCommands() {
    const mover = (passo: number) => () => ({ editor, commands }: { editor: { getAttributes: (t: string) => Record<string, unknown> }; commands: { updateAttributes: (t: string, a: Record<string, unknown>) => boolean } }) => {
      const tipo = editor.getAttributes("heading").level ? "heading" : "paragraph";
      const atual = Number(editor.getAttributes(tipo).recuo ?? 0);
      const novo = Math.min(Math.max(atual + passo, 0), CLASSES_DE_RECUO.length);
      if (novo === atual) return false;
      return commands.updateAttributes(tipo, { recuo: novo });
    };
    return { aumentarRecuo: mover(1), diminuirRecuo: mover(-1) };
  },
});

/**
 * Lista de tarefas — **sem `<input>`**.
 *
 * A `TaskItem` pronta serializa `<li data-checked="true"><label><input
 * type="checkbox" checked>`, e nada disso sobrevive ao saneador: `input` e
 * `label` não são tags permitidas e `checked` e `data-checked` não são
 * atributos permitidos (RN-41). Uma nota salva com tarefas voltaria como uma
 * lista de itens sem caixa nenhuma — perda silenciosa, dentro do conteúdo do
 * usuário, que é exatamente o que o invariante 17 existe para impedir.
 *
 * Então o estado vira classe no `<li>`, e a caixa é desenhada pela folha. A
 * interação continua sendo a da extensão: o `nodeView` dela é quem marca e
 * desmarca dentro do editor, e a serialização é o que muda.
 */
export const TarefaSemInput = TaskItem.extend({
  parseHTML() {
    return [{ tag: "li.rico-tarefa", priority: 60 }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const feita = node.attrs.checked === true;
    return ["li", mergeAttributes(HTMLAttributes, { class: feita ? "rico-tarefa rico-feita" : "rico-tarefa" }), 0];
  },

  addAttributes() {
    return {
      checked: {
        default: false,
        // Lido da classe, não de `data-checked`: é a classe que atravessa o
        // saneador, então é a classe que precisa ser a fonte da verdade.
        parseHTML: (el) => el.classList.contains("rico-feita"),
        renderHTML: () => ({}),
        keepOnSplit: false,
      },
    };
  },
});

export const ListaDeTarefas = TaskList.extend({
  parseHTML() {
    return [{ tag: "ul.rico-tarefas", priority: 60 }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["ul", mergeAttributes(HTMLAttributes, { class: "rico-tarefas" }), 0];
  },
});

/**
 * Tabela — e **duas coisas** tiveram de sair para ela passar no portão de CSP.
 *
 * A primeira é `resizable`: ligado, ele faz a célula guardar `colwidth` e
 * serializar `style="width: …"`. A segunda só apareceu quando o portão rodou:
 * mesmo com `resizable: false`, o `renderHTML` que vem pronto escreve
 * `style="min-width"` na `<table>` e monta um `<colgroup>` com um
 * `style="min-width"` por coluna. Estilo inline dentro do conteúdo do usuário
 * é o que a CSP bloqueia e o que o saneador descarta — a tabela chegaria ao
 * banco sem ele de qualquer jeito, e a diferença é que assim ela chega igual ao
 * que o editor mostrou.
 *
 * O `renderHTML` próprio devolve a tabela mínima: `<table class="rico-tabela">`
 * com um `<tbody>` dentro. A largura das colunas passa a ser a do conteúdo, que
 * é o que uma tabela de nota de estudo precisa.
 */
export const TabelaSemEstilo = Table.extend({
  renderHTML() {
    return ["table", { class: "rico-tabela" }, ["tbody", 0]];
  },
});

export const TABELA = [
  TabelaSemEstilo.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
];

/** A extensão de placeholder, com o texto que a Fase 2 pede. */
export const ESPACO_RESERVADO = Placeholder.configure({ placeholder: "Comece a escrever…" });

/** Tudo o que o editor do caderno carrega além do `StarterKit`. */
export const EXTENSOES_DO_CADERNO = [
  CorDoTexto,
  Realce,
  TamanhoDoTexto,
  FonteDoTexto,
  Alinhamento,
  Recuo,
  ListaDeTarefas,
  TarefaSemInput,
  ...TABELA,
  ESPACO_RESERVADO,
];
